import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Upload, Clock, History as HistoryIcon, ChevronDown, ChevronRight, RefreshCcw } from "lucide-react";
import { authClient } from "../../lib/auth";

const WEEKDAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

export default function TrustAutomationSection() {
  return (
    <section className="mb-16 border border-bl-border p-8" data-testid="admin-trust-automation">
      <span className="overline">Automatisation & audit</span>
      <h2 className="font-serif text-2xl mt-1 mb-6">Planification, historique &amp; import CSV</h2>

      <div className="grid lg:grid-cols-2 gap-6">
        <ScheduleCard />
        <ImportCsvCard />
      </div>
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <ReferenceScrapeCard />
        <HistoryCard />
      </div>
      <div className="grid lg:grid-cols-1 gap-6 mt-6">
        <PreviewHistoryCard />
      </div>
    </section>
  );
}

/* ------------- SCHEDULE ------------- */
const ScheduleCard = () => {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await authClient.get("/admin/trust/schedule");
    setState(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await authClient.put("/admin/trust/schedule", next);
      setState(data);
    } finally { setSaving(false); }
  };

  if (!state) return <Card><Loader2 size={14} className="animate-spin text-bl-textSoft" /></Card>;

  return (
    <Card title="Rescoring automatique" icon={<Clock size={14} strokeWidth={1.6} />}>
      <label className="flex items-center gap-2 cursor-pointer" data-testid="schedule-enabled">
        <input
          type="checkbox"
          checked={state.enabled}
          onChange={(e) => save({ ...state, enabled: e.target.checked })}
          className="accent-bl-fuchsia w-4 h-4"
        />
        <span className="text-sm font-medium">Activer le calcul hebdomadaire</span>
      </label>

      <div className={`mt-4 grid grid-cols-2 gap-3 ${state.enabled ? "" : "opacity-50 pointer-events-none"}`}>
        <div>
          <label className="text-xs uppercase tracking-wide text-bl-textSoft block mb-1">Jour</label>
          <select
            value={state.weekday}
            onChange={(e) => save({ ...state, weekday: parseInt(e.target.value, 10) })}
            className="w-full border border-bl-border bg-white px-2 py-1.5 text-sm"
            data-testid="schedule-weekday"
          >
            {WEEKDAYS.map((w, i) => <option key={i} value={i}>{w}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-bl-textSoft block mb-1">Heure UTC</label>
          <select
            value={state.hour}
            onChange={(e) => save({ ...state, hour: parseInt(e.target.value, 10) })}
            className="w-full border border-bl-border bg-white px-2 py-1.5 text-sm"
            data-testid="schedule-hour"
          >
            {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
          </select>
        </div>
      </div>

      <div className="mt-4 text-xs text-bl-textSoft space-y-1">
        <p>Prochain : <span className="text-bl-mauveDeep font-medium" data-testid="schedule-next">{fmt(state.next_run) || "—"}</span></p>
        <p>Dernier : <span className="text-bl-mauveDeep font-medium">{fmt(state.last_run) || "jamais"}</span></p>
        {saving && <p className="text-bl-fuchsia">Enregistrement…</p>}
      </div>

      <div className="mt-4 border-t border-bl-border pt-3">
        <label className="text-xs uppercase tracking-wide text-bl-textSoft block mb-1">Destinataire</label>
        <div className="flex gap-2">
          <input
            type="email"
            placeholder="rapport@beckyliss.com"
            value={state.email || ""}
            onChange={(e) => setState({ ...state, email: e.target.value })}
            onBlur={() => save(state)}
            className="flex-1 border border-bl-border bg-white px-2 py-1.5 text-sm"
            data-testid="schedule-email"
          />
          <TestEmailButton email={state.email} />
        </div>
        <label className="text-xs uppercase tracking-wide text-bl-textSoft block mt-2 mb-1">Expéditeur (once domain verified)</label>
        <input
          type="email"
          placeholder="onboarding@resend.dev"
          value={state.sender_email || ""}
          onChange={(e) => setState({ ...state, sender_email: e.target.value })}
          onBlur={() => save(state)}
          className="w-full border border-bl-border bg-white px-2 py-1.5 text-sm"
          data-testid="schedule-sender-email"
        />
        <p className="text-[10px] text-bl-textSoft mt-1 leading-tight">
          Laissez vide pour utiliser <code>onboarding@resend.dev</code> (mode test).
          Une fois votre domaine vérifié sur Resend, saisissez <code>noreply@beckyliss-glossary.com</code>.
        </p>
      </div>
    </Card>
  );
};

const TestEmailButton = ({ email }) => {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const trigger = async () => {
    if (!email) return;
    setBusy(true);
    setMsg("");
    try {
      await authClient.post("/admin/trust/test-email", { email });
      setMsg("✔ envoyé");
    } catch (e) {
      setMsg(e.response?.data?.detail?.slice(0, 40) || "erreur");
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(""), 4000);
    }
  };
  return (
    <button
      onClick={trigger}
      disabled={!email || busy}
      className="text-xs border border-bl-mauveDeep text-bl-mauveDeep px-2 py-1 hover:bg-bl-mauveDeep hover:text-white transition-colors disabled:opacity-40"
      data-testid="schedule-test-email"
      title={msg || "Envoyer un email de test"}
    >
      {busy ? "…" : msg || "Test"}
    </button>
  );
};

/* ------------- CSV IMPORT ------------- */
const ImportCsvCard = () => {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const doImport = async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await authClient.post("/admin/trust/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
    } catch (e) {
      setResult({ error: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  return (
    <Card title="Import CSV maquillage" icon={<Upload size={14} strokeWidth={1.6} />}>
      <div
        className="border border-dashed border-bl-border p-4 text-center cursor-pointer hover:border-bl-fuchsia transition-colors bg-white"
        onClick={() => ref.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files?.[0];
          if (f) setFile(f);
        }}
        data-testid="csv-dropzone"
      >
        <Upload size={18} className="mx-auto text-bl-textSoft mb-2" strokeWidth={1.5} />
        <p className="text-xs text-bl-textSoft">
          {file ? <span className="font-medium text-bl-mauveDeep">{file.name}</span> : "Glissez un fichier .csv ou cliquez"}
        </p>
        <input
          ref={ref}
          type="file"
          accept=".csv,.txt"
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0])}
          data-testid="csv-file-input"
        />
      </div>

      <div className="mt-3 text-[10px] text-bl-textSoft leading-relaxed">
        Colonnes attendues : <code>slug, name, global_score, bio, securite_eu, pigments, hydratation, vegetal, sans_controverses, pills</code> (pastilles séparées par <code>;</code>).
      </div>

      <button
        onClick={doImport}
        disabled={!file || busy}
        className="mt-3 w-full bg-bl-text text-bl-bg py-2 text-sm hover:bg-bl-dore transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
        data-testid="csv-import-btn"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
        Importer
      </button>

      {result && (
        <div className="mt-3 text-xs bg-bl-soft border border-bl-border p-2" data-testid="csv-import-result">
          {result.error ? (
            <span className="text-red-600">{result.error}</span>
          ) : (
            <span>
              <b>{result.imported}</b> importés · {result.skipped_not_found} slug inconnu ·
              {" "}{result.skipped_no_slug} sans slug · {result.errors} erreurs
            </span>
          )}
        </div>
      )}
    </Card>
  );
};

/* ------------- REFERENCE SCRAPE (beckylisscosmetics-beauty-finder.com) ------------- */
const ReferenceScrapeCard = () => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await authClient.get("/admin/trust/scrape-history");
      setHistory(data.history || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const trigger = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await authClient.post("/admin/trust/scrape-reference");
      setResult(data);
      loadHistory();
    } catch (e) {
      setResult({ error: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  return (
    <Card title="Aligner sur beckylisscosmetics-beauty-finder.com"
          icon={<RefreshCcw size={14} strokeWidth={1.6} />}>
      <p className="text-xs text-bl-textSoft leading-relaxed">
        Récupère les <b>trustScore</b> officiels du site de référence et les applique
        aux produits <b>maquillage</b> (teint / yeux / lèvres). Score plancher : <b>8.0</b>.
        Une tentative automatique a lieu chaque heure jusqu'à réussite.
      </p>

      <button
        onClick={trigger}
        disabled={busy}
        className="mt-3 w-full bg-bl-mauveDeep text-white py-2 text-sm hover:bg-bl-fuchsia transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
        data-testid="scrape-reference-btn"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
        Lancer l'alignement maintenant
      </button>

      {result && (
        <div className="mt-3 text-xs bg-bl-soft border border-bl-border p-2"
             data-testid="scrape-reference-result">
          {result.error ? (
            <span className="text-red-600">{result.error}</span>
          ) : result.reference_reachable ? (
            <span>
              <b className="text-bl-mauveDeep">{result.updated}</b> produits alignés ·
              {" "}{result.matched} correspondances sur {result.local_total} ·
              {" "}{result.reference_scored} scores lus sur la référence
            </span>
          ) : (
            <span className="text-amber-700">
              Site de référence injoignable (502). Nouvelle tentative automatique dans l'heure.
            </span>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-3 text-[10px] text-bl-textSoft border-t border-bl-border pt-2 max-h-32 overflow-y-auto">
          <div className="uppercase tracking-wide mb-1">Dernières tentatives</div>
          {history.slice(0, 6).map((h, i) => (
            <div key={i} className="flex items-center justify-between py-0.5">
              <span className="truncate">{fmt(h.started_at)}</span>
              <span className={h.reference_reachable ? "text-green-700" : "text-amber-700"}>
                {h.reference_reachable ? `${h.updated || 0} MAJ` : "502"}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

/* ------------- HISTORY ------------- */
const HistoryCard = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    authClient.get("/admin/trust/history").then(({ data }) => {
      setItems(data.items || []);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <Card title="Historique des changements" icon={<HistoryIcon size={14} strokeWidth={1.6} />}>
      {loading ? (
        <Loader2 size={14} className="animate-spin text-bl-textSoft" />
      ) : items.length === 0 ? (
        <p className="text-xs text-bl-textSoft">Aucun changement enregistré pour le moment.</p>
      ) : (
        <ul className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="history-list">
          {items.map((h, i) => {
            const nBaselines = Object.keys(h.diff?.baselines || {}).length;
            const nPills = Object.keys(h.diff?.pills || {}).length;
            const isOpen = openIdx === i;
            return (
              <li key={i} className="border border-bl-border">
                <button
                  onClick={() => setOpenIdx(isOpen ? null : i)}
                  className="w-full flex items-center justify-between px-2.5 py-2 text-left text-xs hover:bg-bl-soft"
                >
                  <span className="flex items-center gap-2">
                    {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    <span className="font-medium text-bl-mauveDeep">{fmt(h.at)}</span>
                    <span className="text-bl-textSoft truncate max-w-[110px]">{h.user_email}</span>
                  </span>
                  <span className="text-[10px] text-bl-textSoft">
                    {nBaselines}b · {nPills}p
                  </span>
                </button>
                {isOpen && (
                  <div className="px-2.5 py-2 bg-bl-soft text-[11px] space-y-2 max-h-56 overflow-y-auto">
                    {Object.entries(h.diff?.baselines || {}).map(([slug, { before, after }]) => (
                      <div key={slug}>
                        <b>{slug}</b> — baseline :{" "}
                        <span className="text-red-600">[{(before || []).join(",")}]</span>{" → "}
                        <span className="text-green-700">[{(after || []).join(",")}]</span>
                      </div>
                    ))}
                    {Object.entries(h.diff?.pills || {}).map(([slug, { before, after }]) => (
                      <div key={slug}>
                        <b>{slug}</b> — pastilles :{" "}
                        <span className="text-red-600">{(before || []).join(" · ")}</span>{" → "}
                        <span className="text-green-700">{(after || []).join(" · ")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
};

/* ------------- PREVIEW HISTORY ------------- */
const PreviewHistoryCard = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authClient.get("/admin/trust/preview-history");
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Card title="Historique des simulations" icon={<HistoryIcon size={14} strokeWidth={1.6} />}>
      <div className="flex justify-end mb-2">
        <button onClick={load} className="text-[11px] text-bl-mauveDeep hover:text-bl-fuchsia">
          Actualiser
        </button>
      </div>
      {loading ? (
        <Loader2 size={14} className="animate-spin text-bl-textSoft" />
      ) : items.length === 0 ? (
        <p className="text-xs text-bl-textSoft">Aucune simulation enregistrée.</p>
      ) : (
        <ul className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="preview-history-list">
          {items.map((h, i) => (
            <li key={i} className="border border-bl-border px-2.5 py-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="font-medium text-bl-mauveDeep">{fmt(h.at)}</span>
                <span className="text-[10px] text-bl-textSoft">{h.category}</span>
              </div>
              <div className="text-[11px] text-bl-textSoft mt-1">
                Baseline : <span className="text-bl-text">[{(h.baseline || []).join(",")}]</span>
              </div>
              <div className="mt-1 space-y-0.5">
                {(h.samples || []).map((s, j) => (
                  <div key={j} className="flex items-center gap-1 text-[10.5px]">
                    <span className="text-bl-textSoft truncate max-w-[160px]">{s.name}</span>
                    <span className="text-red-500">{s.current_score ?? "—"}</span>
                    <span>→</span>
                    <span className="text-green-700 font-semibold">{s.proposed_score}</span>
                  </div>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

/* ------------- Shared ------------- */
const Card = ({ title, icon, children }) => (
  <div className="border border-bl-border p-4 bg-white/70">
    {title && (
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="font-serif text-base text-bl-text">{title}</h3>
      </div>
    )}
    {children}
  </div>
);

const fmt = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};
